import { Component } from '@theme/component';
import { prefersReducedMotion, requestIdleCallback } from '@theme/utilities';

const MOBILE_BREAKPOINT = 749;

/**
 * @typedef {object} Refs
 * @property {HTMLImageElement} imageA
 * @property {HTMLImageElement} imageB
 * @property {HTMLElement[]} [frame]
 *
 * @extends {Component<Refs>}
 */
export class ScrollSequenceHero extends Component {
  requiredRefs = ['imageA', 'imageB'];

  /**
   * @type {{ desktop: { frame: HTMLElement, src: string }[], mobile: { frame: HTMLElement, src: string }[] }}
   */
  #manualFrames = { desktop: [], mobile: [] };

  /**
   * @type {HTMLImageElement | null}
   */
  #manualDisplayImage = null;

  /**
   * @type {string}
   */
  #manualActiveGroup = '';

  /**
   * @type {number}
   */
  #manualActiveIndex = -1;

  /**
   * @type {string}
   */
  #manualTargetGroup = '';

  /**
   * @type {number}
   */
  #manualTargetIndex = -1;

  /**
   * @type {{ position: number, url?: string }[]}
   */
  #frames = [];

  /**
   * @type {Map<string, HTMLImageElement>}
   */
  #images = new Map();

  /**
   * @type {Map<string, Promise<HTMLImageElement>>}
   */
  #imagePromises = new Map();

  /**
   * @type {number}
   */
  #animationFrame = 0;

  /**
   * @type {number}
   */
  #version = 0;

  /**
   * @type {number}
   */
  #currentAIndex = -1;

  /**
   * @type {number}
   */
  #currentBIndex = -1;

  /**
   * @type {number}
   */
  #lastBlendT = 0;

  /**
   * @type {boolean}
   */
  #forceSync = false;

  #mobileMedia = matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
  #reducedMotionMedia = matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();

    addMediaQueryListener(this.#mobileMedia, this.#handleEnvironmentChange);
    addMediaQueryListener(this.#reducedMotionMedia, this.#handleEnvironmentChange);
    document.addEventListener('shopify:block:select', this.#handleBlockSelect);

    this.#startPolling();

    requestIdleCallback(() => {
      this.updatedCallback();
    });
  }

  updatedCallback() {
    super.updatedCallback();

    if (this.dataset.renderMode === 'manual') {
      this.#refreshManual();
      return;
    }

    this.#refresh();
  }

  disconnectedCallback() {
    removeMediaQueryListener(this.#mobileMedia, this.#handleEnvironmentChange);
    removeMediaQueryListener(this.#reducedMotionMedia, this.#handleEnvironmentChange);
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);

    cancelAnimationFrame(this.#animationFrame);
    this.#animationFrame = 0;

    super.disconnectedCallback();
  }

  #refresh() {
    const version = ++this.#version;

    this.#frames = this.#readFrames();
    this.#currentAIndex = -1;
    this.#currentBIndex = -1;
    this.dataset.ready = 'false';

    if (!this.#shouldAnimate()) {
      this.dataset.mode = 'fallback';
      return;
    }

    this.dataset.mode = 'sequence';

    if (this.#hasManualFrames()) {
      this.#showManualFrame(0, 0, 0);
      this.#currentAIndex = 0;
      this.#currentBIndex = 0;
      this.dataset.ready = 'true';
      this.#requestSync(true);
      return;
    }

    requestAnimationFrame(() => {
      if (!this.isConnected || version !== this.#version) return;

      const firstUrl = this.#frames[0]?.url;
      if (firstUrl) {
        this.#showBlendedImages(firstUrl, firstUrl, 0);
        this.#requestSync(true);
      } else {
        this.dataset.mode = 'fallback';
      }

      requestIdleCallback(() => {
        if (!this.isConnected || version !== this.#version) return;
        this.#preloadRemainingFrames();
      });
    });
  }

  #refreshManual() {
    this.#manualDisplayImage = this.querySelector('.scroll-sequence-hero__manual-display-image');
    this.#manualFrames = this.#readManualFrameGroups();
    this.#manualActiveGroup = '';
    this.#manualActiveIndex = -1;
    this.#manualTargetGroup = '';
    this.#manualTargetIndex = -1;
    this.dataset.ready = 'false';

    const { group, frames } = this.#currentManualSet();

    if (!frames.length) {
      this.dataset.mode = 'fallback';
      return;
    }

    this.dataset.mode = 'sequence';

    const firstSrc = frames[0]?.src;

    if (!firstSrc) {
      this.dataset.mode = 'fallback';
      return;
    }

    this.#loadImage(firstSrc)
      .then(() => {
        if (!this.isConnected || this.dataset.renderMode !== 'manual') return;

        this.#applyManualFrame(group, 0);
        this.#requestSync(true);

        requestIdleCallback(() => {
          if (!this.isConnected || this.dataset.renderMode !== 'manual') return;
          this.#preloadRemainingManualFrames();
        });
      })
      .catch(() => {
        if (!this.isConnected) return;
        this.dataset.mode = 'fallback';
      });
  }

  #readFrames() {
    const frameRefs = this.refs.frame;
    const elements = Array.isArray(frameRefs) ? frameRefs : frameRefs ? [frameRefs] : [];
    const manualFrames = elements.flatMap((element) => {
      if (!(element instanceof HTMLElement)) return [];

      const { framePosition } = element.dataset;
      return [{ position: Number(framePosition) || 0 }];
    });

    if (manualFrames.length) return manualFrames;

    const generatedFrames = this.#readGeneratedFrames();
    if (generatedFrames.length) return generatedFrames;

    return [];
  }

  #readManualFrameGroups() {
    return {
      desktop: this.#readManualFrameSelector('.scroll-sequence-hero__frames--desktop .scroll-sequence-hero__manual-frame'),
      mobile: this.#readManualFrameSelector('.scroll-sequence-hero__frames--mobile .scroll-sequence-hero__manual-frame'),
    };
  }

  /**
   * @param {string} selector
   * @returns {{ frame: HTMLElement, src: string }[]}
   */
  #readManualFrameSelector(selector) {
    return Array.from(this.querySelectorAll(selector)).flatMap((frame) => {
      if (!(frame instanceof HTMLElement)) return [];

      const image = frame.querySelector('.scroll-sequence-hero__manual-frame-image');
      if (!(image instanceof HTMLImageElement)) return [];

      const src = image.getAttribute('src') || image.src || '';
      if (!src) return [];

      return [{ frame, src }];
    });
  }

  #readGeneratedFrames() {
    const {
      sequencePrefix = '',
      sequenceStart = '',
      sequenceEnd = '',
      sequencePadding = '',
      sequenceExtension = '',
      sequenceReferenceUrl = '',
    } = this.dataset;

    const prefix = `${sequencePrefix}`.trim();
    const extension = `${sequenceExtension}`.trim();
    const start = Number(sequenceStart);
    const end = Number(sequenceEnd);
    const padding = Number(sequencePadding);

    if (!prefix || !extension || !sequenceReferenceUrl || Number.isNaN(start) || Number.isNaN(end) || Number.isNaN(padding)) {
      return [];
    }

    if (start < 1 || end < start || padding < 1) return [];

    let baseUrl;

    try {
      baseUrl = new URL(sequenceReferenceUrl, window.location.origin);
    } catch {
      return [];
    }

    const lastSlashIndex = baseUrl.pathname.lastIndexOf('/');
    if (lastSlashIndex === -1) return [];

    const directoryPath = baseUrl.pathname.slice(0, lastSlashIndex + 1);
    const width = baseUrl.searchParams.get('width');

    return Array.from({ length: end - start + 1 }, (_, offset) => {
      const frameNumber = start + offset;
      const filename = `${prefix}${String(frameNumber).padStart(padding, '0')}.${extension}`;
      const url = new URL(`${directoryPath}${filename}`, baseUrl.origin);

      if (width) url.searchParams.set('width', width);

      return {
        position: offset,
        url: url.toString(),
      };
    });
  }

  #shouldAnimate() {
    return !this.#mobileMedia.matches && !prefersReducedMotion() && this.#frames.length > 1;
  }

  #preloadRemainingFrames() {
    if (this.#hasManualFrames()) return;

    this.#frames.slice(1).forEach((_, index) => {
      this.#loadFrame(index + 1).catch(() => {});
    });
  }

  #preloadRemainingManualFrames() {
    const desktopFrames = this.#manualFrames.desktop.slice(1);
    const mobileFrames = this.#manualFrames.mobile.slice(1);

    [...desktopFrames, ...mobileFrames].forEach((frame) => {
      this.#loadImage(frame.src).catch(() => {});
    });
  }

  /**
   * @param {number} index
   * @returns {Promise<HTMLImageElement>}
   */
  #loadFrame(index) {
    const frame = this.#frames[index];

    if (!frame?.url) {
      return Promise.reject(new Error(`Missing frame at index ${index}`));
    }

    return this.#loadImage(frame.url);
  }

  /**
   * @param {string} url
   * @returns {Promise<HTMLImageElement>}
   */
  #loadImage(url) {
    const cachedImage = this.#images.get(url);
    if (cachedImage) return Promise.resolve(cachedImage);

    const existingPromise = this.#imagePromises.get(url);
    if (existingPromise) return existingPromise;

    const promise = new Promise((resolve, reject) => {
      const image = new Image();
      image.decoding = 'async';

      image.addEventListener('load', () => {
        this.#images.set(url, image);
        resolve(image);
      });

      image.addEventListener('error', () => {
        this.#imagePromises.delete(url);
        reject(new Error(`Failed to load frame: ${url}`));
      });

      image.src = url;
    });

    this.#imagePromises.set(url, promise);
    return promise;
  }

  #handleEnvironmentChange = () => {
    if (this.dataset.renderMode === 'manual') {
      this.#refreshManual();
      return;
    }

    this.#refresh();
  };

  #startPolling = () => {
    if (!this.isConnected) return;
    this.#requestSync();
    requestAnimationFrame(this.#startPolling);
  };

  /**
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    if (!window.Shopify?.designMode || this.dataset.mode !== 'sequence' || !(event.target instanceof HTMLElement)) return;

    const frame = event.target.closest('.scroll-sequence-hero__manual-frame');
    if (!(frame instanceof HTMLElement) || frame.closest('scroll-sequence-hero-component') !== this) return;

    if (this.dataset.renderMode === 'manual') {
      const desktopIndex = this.#manualFrames.desktop.findIndex((item) => item.frame === frame);
      if (desktopIndex !== -1) {
        this.#applyManualFrame('desktop', desktopIndex);
        return;
      }

      const mobileIndex = this.#manualFrames.mobile.findIndex((item) => item.frame === frame);
      if (mobileIndex !== -1) {
        this.#applyManualFrame('mobile', mobileIndex);
      }

      return;
    }

    const position = Number(frame.dataset.framePosition);
    const frameIndex = this.#frames.findIndex((item) => item.position === position);
    if (frameIndex === -1) return;

    this.#showManualFrame(frameIndex, frameIndex, 0);
    this.#currentAIndex = frameIndex;
    this.#currentBIndex = frameIndex;
    this.dataset.ready = 'true';
  };

  /**
   * @param {boolean} [force=false]
   */
  #requestSync(force = false) {
    if (this.dataset.mode !== 'sequence') return;

    this.#forceSync = this.#forceSync || force;
    if (this.#animationFrame) return;

    this.#animationFrame = requestAnimationFrame(() => {
      const forceDraw = this.#forceSync;

      this.#animationFrame = 0;
      this.#forceSync = false;

      if (this.dataset.renderMode === 'manual') {
        this.#syncManualFrame(forceDraw);
        return;
      }

      this.#syncFrame(forceDraw);
    });
  }

  /**
   * @param {boolean} force
   */
  #syncManualFrame(force) {
    const { group, frames } = this.#currentManualSet();
    if (!frames.length) return;

    const frameIndex = Math.round(this.#scrollProgress() * (frames.length - 1));
    if (!force && group === this.#manualActiveGroup && frameIndex === this.#manualActiveIndex) return;

    const targetFrame = frames[frameIndex];
    if (!targetFrame?.src) return;

    this.#manualTargetGroup = group;
    this.#manualTargetIndex = frameIndex;

    this.#loadImage(targetFrame.src)
      .then(() => {
        if (!this.isConnected || this.dataset.renderMode !== 'manual') return;
        if (this.#manualTargetGroup !== group || this.#manualTargetIndex !== frameIndex) return;

        this.#applyManualFrame(group, frameIndex);
      })
      .catch(() => {});
  }

  /**
   * @param {boolean} force
   */
  #syncFrame(force) {
    if (this.dataset.mode !== 'sequence') return;

    const progress = this.#scrollProgress();
    const safePos = progress * (this.#frames.length - 1);

    const idxA = Math.floor(safePos);
    const blendT = safePos - idxA;
    const idxB = Math.min(idxA + 1, this.#frames.length - 1);

    if (!force && idxA === this.#currentAIndex && idxB === this.#currentBIndex && Math.abs(blendT - (this.#lastBlendT || 0)) < 0.001) return;
    this.#lastBlendT = blendT;

    this.dataset.ready = 'true';
    this.dataset.debugProgress = progress.toFixed(3);
    this.dataset.debugFrames = `${idxA}-${idxB}`;

    if (this.#hasManualFrames()) {
      this.#showManualFrame(idxA, idxB, blendT);
      this.#currentAIndex = idxA;
      this.#currentBIndex = idxB;
      return;
    }

    const urlA = this.#frames[idxA]?.url;
    const urlB = this.#frames[idxB]?.url;

    if (urlA && urlB) {
      this.#showBlendedImages(urlA, urlB, blendT);
      this.#currentAIndex = idxA;
      this.#currentBIndex = idxB;
    }
  }

  #scrollProgress() {
    const scrollableDistance = Math.max(this.offsetHeight - window.innerHeight, 1);
    const progress = clamp(-this.getBoundingClientRect().top / scrollableDistance, 0, 1);
    return progress;
  }

  /**
   * @param {string} srcA
   * @param {string} srcB
   * @param {number} blendT
   */
  #showBlendedImages(srcA, srcB, blendT) {
    const { imageA, imageB } = this.refs;

    if (imageA.getAttribute('src') !== srcA) {
      imageA.setAttribute('src', srcA);
    }
    if (imageB.getAttribute('src') !== srcB) {
      imageB.setAttribute('src', srcB);
    }

    imageA.style.opacity = String(1 - blendT);
    imageB.style.opacity = String(blendT);
  }

  /**
   * @param {number} idxA
   * @param {number} idxB
   * @param {number} blendT
   */
  #showManualFrame(idxA, idxB, blendT) {
    const frameRefs = this.refs.frame;
    const elements = Array.isArray(frameRefs) ? frameRefs : frameRefs ? [frameRefs] : [];

    elements.forEach((element, index) => {
      if (!(element instanceof HTMLElement)) return;

      if (index === idxA) {
        element.style.opacity = String(1 - blendT);
        element.setAttribute('data-active', 'true');
      } else if (index === idxB && idxA !== idxB) {
        element.style.opacity = String(blendT);
        element.setAttribute('data-active', 'true');
      } else {
        element.style.opacity = '0';
        element.removeAttribute('data-active');
      }
    });
  }

  #hasManualFrames() {
    const frameRefs = this.refs.frame;
    const elements = Array.isArray(frameRefs) ? frameRefs : frameRefs ? [frameRefs] : [];

    return elements.length > 0;
  }

  #currentManualSet() {
    if (this.#mobileMedia.matches && this.#manualFrames.mobile.length) {
      return {
        group: 'mobile',
        frames: this.#manualFrames.mobile,
      };
    }

    if (this.#manualFrames.desktop.length) {
      return {
        group: 'desktop',
        frames: this.#manualFrames.desktop,
      };
    }

    return {
      group: 'mobile',
      frames: this.#manualFrames.mobile,
    };
  }

  /**
   * @param {string} group
   * @param {number} index
   */
  #applyManualFrame(group, index) {
    const frames = this.#manualFrames[group] || [];
    const frame = frames[index];
    if (!frame?.src) return;

    if (this.#manualDisplayImage) {
      this.#manualDisplayImage.setAttribute('src', frame.src);
    }

    [...this.#manualFrames.desktop, ...this.#manualFrames.mobile].forEach((item) => {
      if (item.frame === frame.frame) {
        item.frame.setAttribute('data-active', 'true');
      } else {
        item.frame.removeAttribute('data-active');
      }
    });

    this.#manualActiveGroup = group;
    this.#manualActiveIndex = index;
    this.dataset.ready = 'true';
  }
}

if (!customElements.get('scroll-sequence-hero-component')) {
  customElements.define('scroll-sequence-hero-component', ScrollSequenceHero);
}

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

/**
 * @param {MediaQueryList} mediaQuery
 * @param {(event: MediaQueryListEvent) => void} listener
 */
function addMediaQueryListener(mediaQuery, listener) {
  if ('addEventListener' in mediaQuery) {
    mediaQuery.addEventListener('change', listener);
    return;
  }

  // @ts-ignore
  mediaQuery.addListener(listener);
}

/**
 * @param {MediaQueryList} mediaQuery
 * @param {(event: MediaQueryListEvent) => void} listener
 */
function removeMediaQueryListener(mediaQuery, listener) {
  if ('removeEventListener' in mediaQuery) {
    mediaQuery.removeEventListener('change', listener);
    return;
  }

  // @ts-ignore
  mediaQuery.removeListener(listener);
}
